import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  getColor,
  Icons,
  InputField,
  Modal,
  Tooltip,
  Typography,
} from "fds";
import "./index.scss";
import useDocumentStore from "../../../../redux/dispatcher/useDocumentStore";
import { Folder } from "../../../../types/files";
import utilsCommon from "../../../../shared/utils/common";
import { t } from "i18next";
import useWorkgroupStore from "../../../../redux/dispatcher/useWorkgroupStore";
import { Workgroup } from "../../../../types/workgroup";
interface CreateNewFolderModalProps {
  open: boolean;
  workgroupId?: string;
  targetFolderId?: string;
  initFolderName?: string;
  onClose: () => void;
}

const CreateNewFolderModal: React.FC<CreateNewFolderModalProps> = ({
  open,
  onClose,
  workgroupId,
  initFolderName = "",
  targetFolderId,
}) => {
  /** Hook 정리 */
  const targetRef = useRef<HTMLSpanElement>(null);
  const { documentState, createFolder, updateFolder } = useDocumentStore();
  const { workgroupState } = useWorkgroupStore();
  /** State정리 */
  const [newFolderName, changeNewFolderName] = useState(initFolderName);
  const [folderInfo, changeFolderInfo] = useState<Folder>();
  const [shownLength, changeShownLength] = useState(100);
  const [err, changeErr] = useState("");
  const [workgroupName, changeWorkgroupName] = useState("HOME");

  /** UseEffect 정리 */
  useEffect(() => {
    changeFolderInfo(documentState.docListInfo.folder);
  }, [documentState.docListInfo]);
  useEffect(() => {
    changeWorkgroupName(
      workgroupState.workgroupSidemenuList?.filter(
        (item: Workgroup) => item.workgroupId === workgroupId
      )[0]?.workgroupName ?? "HOME"
    );
  }, [workgroupState.workgroupSidemenuList]);
  // 폴더 생성, 수정의 상단에 folder path 표시를 위해 계산
  useEffect(() => {
    if (!targetRef.current) return;
    const observer = new ResizeObserver(() => {
      if (targetRef.current) {
        const lastWidth =
          document.getElementById("lastFolder")?.offsetWidth ?? 208;
        const calcWidth =
          targetRef.current.scrollWidth < 440
            ? 100
            : Math.ceil(
                (385 -
                  lastWidth -
                  (folderInfo?.parentFolderList?.length ?? 1) * 20) /
                  100
              ) || 1;
        if (
          shownLength !== calcWidth &&
          shownLength === 100 &&
          calcWidth !== 100
        ) {
          changeShownLength(calcWidth);
        }
      }
    });
    observer.observe(targetRef.current);
    return () => observer.disconnect();
  }, [folderInfo, shownLength]);

  /** 함수 정리 */
  /** 폴더 생성 */
  const handleCreateUpdateFolder = async () => {
    if (utilsCommon.fileNameValidation(newFolderName)) {
      changeErr(t("file.alert.errorFolderName"));
      return;
    }
    let resp =
      initFolderName === ""
        ? await createFolder({
            workgroupId: workgroupId,
            parentFolderId: folderInfo?.folderInfo.folderId,
            folderName: newFolderName,
          })
        : await updateFolder({
            folderId: targetFolderId,
            folderName: newFolderName,
          });
    if (!resp.payload) {
      changeErr(t("file.alert.duplicateFolder"));
    } else {
      onClose();
    }
  };
  /** UI: 폴더 경로 표시
   * 폰트 Max사이즈 없음, 끝 텍스트 최대20자로 노출(208px) ,나머지 텍스트 max: 100px, 호버시 노출
   */
  const handleRenderFolderPath = () => {
    const totalList = folderInfo?.parentFolderList.filter(
      (item1, index1) =>
        index1 === 0 ||
        folderInfo?.parentFolderList.length - shownLength <= index1
    );
    return totalList?.map((item, index) =>
      shownLength === 100 ||
      index === 0 ||
      totalList?.length - shownLength < index
        ? [
            <Tooltip
              message={
                item.folderId === workgroupId ? workgroupName : item.folderName
              }
            >
              <Typography
                type="body4_rg"
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
                ellipsis
                maxWidth={100}
              >
                {item.folderId === workgroupId
                  ? workgroupName
                  : item.folderName}
              </Typography>
            </Tooltip>,
            <Icons
              variant="line"
              label="chevron"
              direction="right"
              size={16}
              stroke={getColor("COLOR_ICON_INTERACTIVE_SECONDARY")}
            />,
          ]
        : [
            <Tooltip
              message={folderInfo?.parentFolderList
                .filter(
                  (item, index) =>
                    index > 0 &&
                    index <= folderInfo?.parentFolderList.length - shownLength
                )
                .map((item) =>
                  item.folderId === workgroupId
                    ? workgroupName
                    : item.folderName
                )
                .join(" > ")}
            >
              <Icons
                variant="line"
                label="moreVertical"
                size={16}
                stroke={getColor("COLOR_ICON_INTERACTIVE_TERTIARY")}
              />
            </Tooltip>,
            <Icons
              variant="line"
              label="chevron"
              direction="right"
              size={16}
              stroke={getColor("COLOR_ICON_INTERACTIVE_SECONDARY")}
            />,
          ]
    );
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        initFolderName === ""
          ? t("file.action.createFolder")
          : t("file.action.rename")
      }
      classes="createNewFolder"
      subTitle={
        <Box width={"calc(100% - 22px)"} ref={targetRef}>
          <Icons variant="fill" label="folder" size={16} classes="mg mr-6" />
          {handleRenderFolderPath()}
          <Tooltip
            message={
              folderInfo?.folderInfo?.folderId === workgroupId
                ? workgroupName
                : folderInfo?.folderInfo?.folderName
            }
          >
            <Box maxWidth={208} id="lastFolder">
              <Typography
                type="body4_rg"
                ellipsis
                maxWidth={208}
                exactColor={getColor("COLOR_TEXT_SECONDARY")}
              >
                {folderInfo?.folderInfo?.folderId === workgroupId
                  ? workgroupName
                  : folderInfo?.folderInfo?.folderName}
              </Typography>
            </Box>
          </Tooltip>
        </Box>
      }
      width={540}
      body={
        <InputField
          placeholder={t("file.folderPlaceholder")}
          size="md"
          value={newFolderName}
          helpMessage={err}
          isError={!!err}
          maxLength={50}
          onChange={(e) => changeNewFolderName(e.target.value)}
        />
      }
      footer={
        <Box>
          <Button
            label={t("common.cancel")}
            btnStyle="fill"
            type="secondary"
            onClick={onClose}
          />
          <Button
            label={t("common.save")}
            btnStyle="fill"
            state={
              newFolderName.trim() === initFolderName ? "disable" : "default"
            }
            onClick={handleCreateUpdateFolder}
          />
        </Box>
      }
    />
  );
};
export default CreateNewFolderModal;
